import React from "react";
import "./ProjectDetailsHeader.scss";
import {
  Alert,
  AlertActionCloseButton,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  ClipboardCopy,
  Dropdown,
  DropdownItem,
  DropdownItemProps,
  DropdownList,
  Grid,
  GridItem,
  MenuToggle,
  MenuToggleElement,
  Split,
  SplitItem,
  Text,
  TextContent,
  TooltipProps,
  capitalize
} from '@patternfly/react-core';
import { CircleIcon, ExternalLinkAltIcon } from "@patternfly/react-icons";
import { isEmpty } from "lodash";
import Env from "utils/env";
import { getComponents, getScheduledMaintenances, getSystemStatus } from "api/statuspage";
import { intlFormat } from "date-fns";
import Uri from "jsuri";
import { useFetch } from "../../../hooks";
import { patchProjectDetails } from "../../../api/pyxis";
import { ErrorPopup, SuccessPopup, useAlertContext } from "../AlertComponent/AlertContext";
import {
  getProjectTypeData,
  ProjectCertificationStatus,
  ProjectContainerKeys,
  ProjectContainerTypes,
  projectDisplayNames,
  ProjectKeys,
  ProjectRedhatKeys,
  ProjectTypes,
  SystemStatus,
  SystemStatusAlertVariant,
} from "../../../constants/project";
import { usePublishContext } from "../../CertWorkflowApp/PublishContext";
import { IsOperatorBundleProject, IsRHELNonContainerized } from "../../../utils/projectUtils";
import { useControllerContext } from "@rhc-shared-components/control-wrapper";
import { useQuery, useQueryClient } from "react-query";
import { ProductKeys } from "../../../constants/products";
import { useParams } from "react-router";
import { CWRouteParams } from "../../../constants/router";
import { IProjectDetails } from "../../../interfaces/project";
import { IRepoDetailsResponse } from "../../../interfaces/repo";
import { RedirectToTrobubleShootingGuide } from "../../../utils/redirect";
import Cookies from "js-cookie";

interface IIncidentDetailsType {
  name: string;
  componentStatus: string;
  incidentStatus: string;
  description: string;
  updatedAt: string;
  link: string;
  scheduledFor?: string;
  scheduledUntil?: string;
  enabled?: boolean;
}

enum EStatusPageStatus {
  "investigating" = "Investigating",
  "identified" = "Identified",
  "monitoring" = "Monitoring",
  "resolved"  = "Resolved",
  "scheduled" = "Scheduled",
  "in_progress" = "In Progress",
  "verifying" = "Verifying",
  "completed" = "Completed"
}

const ProjectDetailsHeader: React.FC = () => {
  const [selectActionDropdownText, setSelectActionDropdownText] =
    React.useState('Actions');
  const [isActionDropdownOpen, toggleActionDropdown] =
    React.useState<boolean>(false);
  const queryClient = useQueryClient();
  const projectDetails = queryClient.getQueryData<IProjectDetails>(['projectDetails']);
  const { setAlertState } = useAlertContext();
  const { request } = useFetch(patchProjectDetails, { propagateErrors: true });
  const componentID = 't1b831tgjrl2';
  const iconClassName = 'project-details-header__secondary__info__icon';
  const { publishState } = usePublishContext();
  const { isReadOnly } = useControllerContext();
  const productInfo = queryClient.getQueryData('productDetails') || {};
  const repoDetails = queryClient.getQueryData<IRepoDetailsResponse>(`repoDetails`)?.data || [] ;
  const isArchiveDisabled = () =>
    publishState ||
    projectDetails?.[ProjectKeys.certificationStatus] ===
    ProjectCertificationStatus.published;
  let actionsOptions = [
    {
      value: 'open_support_case',
      text: 'Open Support Case',
      isDisabled: false,
    },
    {
      value: 'archive_project',
      text: 'Archive Component',
      isDisabled: isArchiveDisabled() || isReadOnly,
    },
  ];

  if (IsOperatorBundleProject(projectDetails)) {
    const option = {
      value: 'remove_from_operator_hub',
      text: 'Remove from Operator Hub',
      isDisabled:
        !IsOperatorBundleProject(
          (projectDetails[ProjectKeys.container] || {})[
            ProjectContainerKeys.type
            ]
        ) && !(!isEmpty(repoDetails) && repoDetails?.length > 0),
    };

    actionsOptions = [...actionsOptions, { ...option }];
  }

  const getComponent = (components) =>
    components.find((el) => el.id === componentID);

  const getFormattedTime = (date) =>
    intlFormat(
      new Date(date),
      {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        timeZoneName: 'short',
        timeZone: 'America/New_York',
      },
      { locale: 'en-US' }
    );
  const {
    data: systemStatus,
  } = useQuery([`systemStatus`], async ({signal}) => {
    const componentStatusResponse = await getComponents(signal);
    const component =  getComponent(componentStatusResponse.components);
    return component?.status;
  });
  const {
    data: incidentDetails,
    isLoading: isStatusFetching
  } = useQuery(['incidentDetails'], async ({signal}) => {
    const response = await getSystemStatus(signal);
    const filteredIncident = response.incidents.find((el) =>
      getComponent(el?.components)
    );
    if (filteredIncident) {
      const incident = getComponent(filteredIncident?.components);
      const savedStatus = Cookies.get(`markedDone${incident?.id}`);
      return {
        id: incident?.id,
        name: filteredIncident?.name,
        componentStatus: incident?.status,
        incidentStatus: filteredIncident?.incident_updates?.[0]?.status,
        description: filteredIncident?.incident_updates?.[0]?.body,
        updatedAt: getFormattedTime(
          filteredIncident.incident_updates?.[0]?.updated_at
        ),
        link: filteredIncident?.shortlink,
        enabled: !savedStatus,
      }
    }
    return null;
  });
  const {
    data: maintenanceDetails,
    isLoading: fetchingMaintenances
  } = useQuery(['maintenanceDetails'], async ({signal}) => {
    const response = await getScheduledMaintenances(signal);
    const filteredIncident = response.scheduled_maintenances.filter((ele) => (ele.status !== 'completed')).find((el) =>
      getComponent(el.components)
    );
    if (filteredIncident) {
      const maintenance = getComponent(filteredIncident.components);
      const savedStatus = Cookies.get(`markedDone${maintenance?.id}`);
      return {
        id: maintenance?.id,
        name: filteredIncident?.name,
        componentStatus: maintenance?.status,
        incidentStatus: filteredIncident?.incident_updates?.[0]?.status,
        description: filteredIncident?.incident_updates?.[0]?.body,
        updatedAt: getFormattedTime(
          filteredIncident?.incident_updates?.[0]?.updated_at
        ),
        link: filteredIncident?.shortlink,
        scheduledFor: getFormattedTime(filteredIncident?.scheduled_for),
        scheduledUntil: getFormattedTime(filteredIncident?.scheduled_until),
        enabled: !savedStatus,
      }
    }
    return null;
  });
  const onToggle = () => {
    toggleActionDropdown(!isActionDropdownOpen);
  };

  const archiveProject = async () => {
    const requestData = {
      _id: projectDetails[ProjectKeys.id],
      project_status: 'archived',
    };
    try {
      const response = await request(
        projectDetails[ProjectKeys.id],
        requestData
      );
      SuccessPopup(
        setAlertState,
        `Archived ${response.name} project successfully`
      );
      window.open(
        `${Env.productListingUrl}/components?component_status=archived`,
        '_self'
      );
    } catch (error: any) {
      ErrorPopup(setAlertState, error.message);
    }
  };

  const onSelect = (event) => {
    const selectedItem = actionsOptions.find(
      (el) => el.text === event.target.textContent,
    );
    setSelectActionDropdownText(selectedItem.text);
    toggleActionDropdown(!isActionDropdownOpen);

    if (event.target.textContent === 'Archive Component') {
      archiveProject();
    }
    if (
      event.target.textContent === 'Open Support Case' ||
      event.target.textContent === 'Remove from Operator Hub'
    ) {
      let TPSDprojectType;
      let supportUrl;

      // TPSD currently only supports ->
      // [Container Image, Operator Bundle Image, Helm Chart] types
      // Ref: https://red.ht/3zKJCsE
      switch (projectDetails[ProjectKeys.type]) {
        case ProjectTypes.Containers: {
          TPSDprojectType =
            projectDetails[ProjectKeys.container][ProjectContainerKeys.type] ===
            ProjectContainerTypes.OPERATOR_BUNDLE_IMAGE
              ? 'Operator Bundle Image'
              : 'Container Image';
          break;
        }
        case ProjectTypes.HelmChart:
          TPSDprojectType = 'Helm Chart';
          break;
        default:
          TPSDprojectType = null;
      }
      supportUrl = new Uri(Env.ConnectPortalUrls[Env.getEnvName()]).setPath(
        '/support/partner-acceleration-desk/#/case/new'
      );

      if (TPSDprojectType) {
        supportUrl = supportUrl
          .addQueryParam('type', 'CERT')
          .addQueryParam('source', 'connect')
          .addQueryParam('cert_project_id', projectDetails[ProjectKeys.id])
          .addQueryParam('cert_project_type', TPSDprojectType);
      }

      if (event.target.text === 'Remove from Operator Hub') {
        supportUrl = supportUrl.addQueryParam(
          'description',
          'My company would like to remove our registry from the Operator Hub'
        );
        supportUrl.addQueryParam(
          'summary',
          `Request to remove ${repoDetails?.[0].repository} from Operator Hub`
        );
      }

      window.open(supportUrl, '_blank');
    }
  };

  const getActions = () =>
    actionsOptions.map((item) => {
      const dropdownProps: DropdownItemProps = {
        id: item.value,
        isDisabled: item.isDisabled,
        style: {
          pointerEvents: 'auto',
          cursor: 'default',
          color: `var(${item.isDisabled ? '--pf-v5-c-dropdown__menu-item--disabled--Color' : '--pf-v5-c-dropdown__menu-item--Color'})`,
        },
      };
      const toolTipProps: TooltipProps = {
        content: null,
        position: 'bottom',
      };
      if (item.isDisabled) {
        if (item.value === 'open_support_case') {
          // Adding popup info for the archive project
          const hasImages = [
            ProjectTypes.Containers,
            ProjectTypes.openStackAppContainer,
          ].includes(projectDetails[ProjectKeys.type]);
          toolTipProps.content = hasImages
            ? 'All images must be unpublished to archive project'
            : 'Project must be unpublished to archive project';
        } else if (item.value === 'remove_from_operator_hub') {
          toolTipProps.content = 'This project doesn\'t have a published operator';
        }
      }
      /* eslint-disable react/jsx-props-no-spreading */
      return (
        <DropdownItem
          {...dropdownProps}
          {...(toolTipProps.content !== null ? { tooltipProps: toolTipProps } : {})}
          key={item.value}
        >
          {item.text}
        </DropdownItem>
      );
    });
  const params = useParams<CWRouteParams>();
  const getSubheading = () => {
    return getProjectTypeData(projectDetails) === 'RHEL' && projectDetails[ProjectKeys.redhat]
    && projectDetails[ProjectKeys.redhat][ProjectRedhatKeys.productVersion] ?
      `Red Hat Enterprise Linux ${projectDetails[ProjectKeys.redhat][ProjectRedhatKeys.productVersion]}
    ${projectDetails[ProjectKeys.container] ? 'Containerized' : 'Non-Containerized'} Application` :
      projectDisplayNames[getProjectTypeData(projectDetails)]
  }
  const isContainerComponent = [
      ProjectTypes.Containers,
      ProjectTypes.openStackInfraContainer,
      ProjectTypes.openStackAppContainer,
      ProjectTypes.RHEL
    ].includes(projectDetails[ProjectKeys.type]) &&
    !IsOperatorBundleProject(projectDetails) &&
    !IsRHELNonContainerized(projectDetails);
  return (
    <Grid className="project-details-header">
      {!isEmpty(incidentDetails) &&
        incidentDetails.enabled &&
        !isStatusFetching && (
          <GridItem span={10} offset={1} className="project-details-header__alert">
            <Alert
              isInline={true}
              title={`${incidentDetails.name} Incident`}
              variant={
                SystemStatusAlertVariant[incidentDetails.componentStatus]
              }
              actionClose={
                <AlertActionCloseButton
                  onClose={() => {
                    Cookies.set(`markedDone${incidentDetails?.id}`, 'done', {
                      expires: 7,
                    });
                    queryClient.setQueryData(['incidentDetails'], { ...incidentDetails, enabled: false });
                  }}
                />
              }>
              {`${capitalize(EStatusPageStatus?.[incidentDetails.incidentStatus] || incidentDetails.incidentStatus)} - ${incidentDetails.description} - ${incidentDetails.updatedAt}`}
              &nbsp;
              <Button
                target="_blank"
                component="a"
                href={incidentDetails.link}
                variant="link"
                isInline>
                View incident report
              </Button>
            </Alert>
          </GridItem>
        )}
      {!isEmpty(maintenanceDetails) &&
        maintenanceDetails.enabled &&
        !fetchingMaintenances && (
          <GridItem span={10} offset={1} className="project-details-header__alert">
            <Alert
              isInline={true}
              title={`${maintenanceDetails.name} Scheduled for ${maintenanceDetails.scheduledFor} - ${maintenanceDetails.scheduledUntil}`}
              variant={
                SystemStatusAlertVariant[maintenanceDetails.incidentStatus]
              }
              actionClose={
                <AlertActionCloseButton
                  onClose={() => {
                    Cookies.set(`markedDone${maintenanceDetails?.id}`, 'done', {
                      expires: 7,
                    });
                    queryClient.setQueryData(['maintenanceDetails'], {
                      ...maintenanceDetails,
                      enabled: false,
                    });
                  }}
                />
              }>
              <Text>
                {`${capitalize(EStatusPageStatus?.[maintenanceDetails.incidentStatus] || maintenanceDetails.incidentStatus )} - ${maintenanceDetails.description}`}
              </Text>
              <Text component="small">{`Posted on - ${maintenanceDetails.updatedAt}   `}</Text>
              <Button
                target="_blank"
                component="a"
                href={maintenanceDetails.link}
                variant="link"
                isInline>
                View maintenance
              </Button>
            </Alert>
          </GridItem>
        )}
      <GridItem span={10} offset={1} className="project-details-header__breadcrumb">
        <Breadcrumb>
          {params?.productId !== Env.productIdStatic ?
            <BreadcrumbItem to={`${Env.productListingUrl}/products`}>
              My Products
            </BreadcrumbItem>:
            <BreadcrumbItem to={`${Env.productListingUrl}/components`}>
              My Components
            </BreadcrumbItem>
          }
          {params?.productId !== Env.productIdStatic &&
            <BreadcrumbItem to={`${Env.productListingUrl}/products/${params?.productId}/components`}>
              {productInfo?.[ProductKeys.productName]}
            </BreadcrumbItem>
          }
          <BreadcrumbItem isActive={true}>{projectDetails[ProjectKeys.name]}</BreadcrumbItem>
        </Breadcrumb>
      </GridItem>
      <GridItem span={10} offset={1} className="project-details-header__primary">
        <Split className="project-details-header__primary__info">
          <SplitItem isFilled>
            <Split className="project-details-header__primary__info--align">
              <SplitItem>
                <TextContent className="project-details-header__primary__info--details">
                  <Text className="project-details-header__primary__info--name">
                    {projectDetails[ProjectKeys.name]}
                  </Text>
                  <Text className="project-details-header__primary__info--type">
                    {getSubheading()}{' '}
                    component
                  </Text>
                </TextContent>
              </SplitItem>
            </Split>
          </SplitItem>
          <SplitItem>
            <TextContent style={{
              paddingRight: `0.5em`
            }}>
              <Text><b>Component PID:</b><ClipboardCopy style={{ background: "transparent" }} variant="inline-compact">{params?.id}</ClipboardCopy></Text>
            </TextContent>
          </SplitItem>
          <SplitItem>
            <Dropdown
              onSelect={onSelect}
              shouldFocusToggleOnSelect
              onOpenChange={() => toggleActionDropdown(!isActionDropdownOpen)}
              toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle ref={toggleRef} onClick={onToggle} isExpanded={isActionDropdownOpen} isFullWidth>
                  {selectActionDropdownText}
                </MenuToggle>
              )}
              isOpen={isActionDropdownOpen}
            >
              <DropdownList>
                {getActions()}
              </DropdownList>
            </Dropdown>
          </SplitItem>
        </Split>
      </GridItem>
      <GridItem span={10} offset={1}>
        <Split className="project-details-header__troubleshooting_guide">
          <SplitItem style={{
            minHeight: '2em'
          }}>
            {isContainerComponent &&
              <Button
                style={{
                  paddingRight: 0,
                  marginRight: 0
                }} variant="link" onClick={RedirectToTrobubleShootingGuide} icon={<ExternalLinkAltIcon />}
                iconPosition="right">
                Troubleshoot component
              </Button>
            }
          </SplitItem>
        </Split>
      </GridItem>
      <GridItem span={10} offset={1} className="project-details-header__secondary">
        <Split className="project-details-header__secondary__info">
          <SplitItem>
            <Split>
              {!isEmpty(systemStatus) && (
                <SplitItem>
                  <Text style={isContainerComponent ? {} : {
                    paddingRight: 0,
                    marginRight: 0
                  }} component="small">
                    <CircleIcon
                      height="8px"
                      width="8px"
                      className={`${iconClassName} ${iconClassName}--${SystemStatusAlertVariant[systemStatus]}`}
                    />
                    {SystemStatus[systemStatus]}
                  </Text>
                </SplitItem>
              )}
              {isContainerComponent && (
                <SplitItem>
                  <Text style={{
                    paddingRight: 0,
                    marginRight: 0
                  }} component="small">
                    <CircleIcon
                      height="8px"
                      width="8px"
                      className={
                        projectDetails?.[ProjectKeys.container]?.[
                          ProjectContainerKeys.autoPublish
                          ]
                          ? `${iconClassName} ${iconClassName}--on`
                          : `${iconClassName} ${iconClassName}--off`
                      }
                    />
                    Auto publish is{' '}
                    {projectDetails?.[ProjectKeys.container]?.[
                      ProjectContainerKeys.autoPublish
                      ]
                      ? 'on'
                      : 'off'}
                  </Text>
                </SplitItem>
              )}
            </Split>
          </SplitItem>
        </Split>
      </GridItem>
    </Grid>
  );
};

export default ProjectDetailsHeader;
